import { Directive, DirectiveBinding } from 'vue'

const DEFAULT_TOUCH_TIMEOUT_MS = 150
const LONG_TOUCH_TIMEOUT_MS = 1000

type TouchDirectiveEl = HTMLElement & {
  _vTouchCleanup?: () => void
  _vTouchUpdateBinding?: (binding: DirectiveBinding) => void
}

const setupTouchListeners = (element: TouchDirectiveEl, binding: DirectiveBinding) => {
  element._vTouchCleanup?.()

  let currentBinding = binding
  let timeoutID: number | undefined

  const onTouchStart = (event: TouchEvent) => {
    const isMultipleTouch = event.touches.length > 1
    if (isMultipleTouch) {
      return
    }
    element.classList.add('select-none')
    if (currentBinding.modifiers.stop) {
      event.stopPropagation()
    }
    let timeout = DEFAULT_TOUCH_TIMEOUT_MS
    if (currentBinding.modifiers.long) {
      timeout = LONG_TOUCH_TIMEOUT_MS
    }
    timeoutID = window.setTimeout(() => {
      currentBinding.value(event, currentBinding.arg)
    }, timeout)
  }

  const onTouchEnd = () => {
    if (timeoutID !== undefined) {
      clearTimeout(timeoutID)
      timeoutID = undefined
    }
    element.classList.remove('select-none')
  }

  element.addEventListener('touchstart', onTouchStart, { passive: true })
  element.addEventListener('touchend', onTouchEnd)

  element._vTouchUpdateBinding = (nextBinding) => {
    currentBinding = nextBinding
  }

  element._vTouchCleanup = () => {
    if (timeoutID !== undefined) {
      clearTimeout(timeoutID)
    }
    element.removeEventListener('touchstart', onTouchStart)
    element.removeEventListener('touchend', onTouchEnd)
    delete element._vTouchCleanup
    delete element._vTouchUpdateBinding
  }
}

export const vTouch: Directive<TouchDirectiveEl> = {
  mounted (element, binding) {
    setupTouchListeners(element, binding)
  },

  updated (element, binding) {
    element._vTouchUpdateBinding?.(binding)
  },

  unmounted (element) {
    element._vTouchCleanup?.()
  },
}
